import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Gavel,
  ShieldAlert,
  FileText,
  UserCheck,
  Scale,
  Download,
  Copy,
  Building2,
  Lock,
  Landmark,
  CheckCircle2,
  AlertTriangle,
  Briefcase,
  FileCheck2,
  Calendar,
  Share2
} from 'lucide-react';
import { ExecutiveLawsuit, PrecautionaryMeasureOptions, AttorneyInfo } from './LitigationTypes';
import { toast } from 'sonner';

interface DebtorOption {
  id?: string;
  unidad_privada: string;
  propietario_nombre: string;
  propietario_id?: string;
  telefono?: string;
  email?: string;
  saldo_capital: number;
  saldo_intereses: number;
  dias_mora?: number;
}

interface ExecutiveLawsuitGeneratorProps {
  debtor: DebtorOption;
  copropiedadNombre?: string;
  copropiedadNit?: string;
  administradorNombre?: string;
  onSaveLawsuit: (lawsuit: ExecutiveLawsuit) => void;
  onCancel: () => void;
}

export const ExecutiveLawsuitGenerator: React.FC<ExecutiveLawsuitGeneratorProps> = ({
  debtor,
  copropiedadNombre = 'Conjunto Residencial Torres del Parque',
  copropiedadNit = '900.852.147-3',
  administradorNombre = 'Administración PH',
  onSaveLawsuit,
  onCancel
}) => {
  // Datos Generales del Proceso
  const [ciudad, setCiudad] = useState('Bogotá D.C.');
  const [departamento, setDepartamento] = useState('Cundinamarca');
  const [administradorCedula, setAdministradorCedula] = useState('52.148.963');

  // Datos del Abogado Apoderado
  const [abogado, setAbogado] = useState<AttorneyInfo>({
    nombre: 'Dr. Alejandro Morales Restrepo',
    cedula: '80.123.456',
    tarjetaProfesional: '245.890 del C.S. de la J.',
    correoJudicial: 'alejandro.morales.abogado@notificacionesjudiciales.co',
    telefono: '3158901234',
    direccionDespacho: 'Carrera 7 No. 72 - 13 Oficina 802, Bogotá D.C.'
  });

  // Datos Inmueble Demandado
  const [matriculaInmobiliaria, setMatriculaInmobiliaria] = useState('50C-1894523');
  const [cedulaCatastral, setCedulaCatastral] = useState('00-01-0024-0012-000');
  const [direccionInmueble, setDireccionInmueble] = useState('Calle 127 No. 19 - 45');

  // Saldos en mora
  const [saldoCapital, setSaldoCapital] = useState(debtor.saldo_capital || 2800000);
  const [saldoIntereses, setSaldoIntereses] = useState(debtor.saldo_intereses || 750000);
  const [mesesAdeudados, setMesesAdeudados] = useState('Expensas comunes ordinarias y extraordinarias correspondientes a los últimos 6 meses');

  // Medidas Cautelares a Solicitar
  const [medidas, setMedidas] = useState<PrecautionaryMeasureOptions>({
    embargoInmueble: true,
    matriculaInmobiliaria: '50C-1894523',
    cedulaCatastral: '00-01-0024-0012-000',
    direccionInmueble: 'Calle 127 No. 19 - 45',
    embargoCuentasBancarias: true,
    limiteEmbargoCuentas: (debtor.saldo_capital || 2800000) * 2, // 200% por costas y futuros
    entidadesBancarias: ['Bancolombia S.A.', 'Banco Davivienda S.A.', 'Banco de Bogotá S.A.', 'Banco BBVA Colombia'],
    embargoSalarios: false,
    empresaEmpleadora: ''
  });

  // Checklist de Anexos
  const [anexos, setAnexos] = useState({
    poderEspecial: true,
    certificadoExistenciaAlcaldia: true,
    tituloEjecutivoCertificacion: true,
    certificadoTradicionLibertad: true,
    liquidacioCreditoDetallada: true,
    pruebaEnvioNotificacionLey2213: true
  });

  // Pestaña de visualización de documentos
  const [docTab, setDocTab] = useState<'demanda' | 'medidas' | 'poder' | 'expediente'>('demanda');

  // Determinación de cuantía según SMLMV 2026 (aprox $1.423.500)
  const cuantiaTotal = saldoCapital + saldoIntereses;
  const tipoCuantia = cuantiaTotal < 56940000 ? 'minima' : cuantiaTotal < 213525000 ? 'menor' : 'mayor';

  // Redacción del Líbelo de Demanda Judicial
  const textoDemanda = useMemo(() => {
    return `SEÑOR(A)
JUEZ CIVIL MUNICIPAL DE ${ciudad.toUpperCase()} (REPARTO)
E. S. D.

ASUNTO: DEMANDA EJECUTIVA SINGULAR DE ${tipoCuantia.toUpperCase()} CUANTÍA (EXPENSAS COMUNES DE PROPIEDAD HORIZONTAL)
DEMANDANTE: PERSONA JURÍDICA ${copropiedadNombre.toUpperCase()} (NIT: ${copropiedadNit})
DEMANDADO(A): ${debtor.propietario_nombre.toUpperCase()} (C.C./NIT: ${debtor.propietario_id || 'POR ESTABLECER'})
INMUEBLE: ${debtor.unidad_privada.toUpperCase()} (MATRÍCULA INMOBILIARIA: ${matriculaInmobiliaria})

${abogado.nombre.toUpperCase()}, mayor de edad, domiciliado y residente en esta ciudad, identificado con la cédula de ciudadanía No. ${abogado.cedula} y portador de la Tarjeta Profesional No. ${abogado.tarjetaProfesional}, actuando en mi condición de apoderado especial de la persona jurídica ${copropiedadNombre.toUpperCase()}, con NIT ${copropiedadNit}, representada legalmente por ${administradorNombre.toUpperCase()}, según consta en el poder especial y certificado de existencia y representación adjuntos, por medio del presente escrito formulo ante su Despacho DEMANDA EJECUTIVA SINGULAR DE ${tipoCuantia.toUpperCase()} CUANTÍA en contra de ${debtor.propietario_nombre.toUpperCase()}, para que por los trámites del Proceso Ejecutivo regulado en el Código General del Proceso (Ley 1564 de 2012, artículos 422 y concordantes) y el artículo 48 de la Ley 675 de 2001, se libre en su contra MANDAMIENTO DE PAGO conforme a las siguientes:

I. PRETENSIONES

PRIMERA: Que se libre Mandamiento de Pago por vía ejecutiva a favor de mi poderdante y en contra del demandado, por la suma de $${saldoCapital.toLocaleString('es-CO')} COP por concepto de saldo de capital insoluto por expensas comunes ordinarias y extraordinarias adeudadas sobre la unidad privada ${debtor.unidad_privada}.

SEGUNDA: Que se condene al demandado al pago de los INTERESES MORATORIOS sobre el capital reclamado, causados a la tasa máxima legal permitida por la ley (equivalente a una y media veces el interés bancario corriente certificado mensualmente por la Superintendencia Financiera de Colombia), liquidados mes a mes desde la fecha en que cada cuota mensual se hizo exigible hasta que se verifique el pago real, total y efectivo de la obligación.

TERCERA: Que de conformidad con lo normado en el artículo 431 del Código General del Proceso, se ordene el pago de todas y cada una de las cuotas de administración ordinarias, extraordinarias, recargos e intereses que se sigan causando en forma sucesiva durante el curso del presente proceso hasta la ejecutoria de la sentencia de remate o liquidación final del crédito.

CUARTA: Que se condene a la parte demandada al pago total de las COSTAS PROCESALES Y AGENCIAS EN DERECHO que se originen con ocasión de la presente acción judicial.

II. HECHOS

PRIMERO: La copropiedad ${copropiedadNombre} es una persona jurídica debidamente constituida bajo el régimen de propiedad horizontal establecido en la Ley 675 de 2001, con personería jurídica y representación legal vigente acreditada ante la Alcaldía Local de ${ciudad}.

SEGUNDO: El (la) demandado(a) ${debtor.propietario_nombre} es el(la) titular del derecho real de dominio y propietario(a) de la unidad privada identificada como ${debtor.unidad_privada}, a la cual le corresponde el folio de Matrícula Inmobiliaria No. ${matriculaInmobiliaria} de la Oficina de Registro de Instrumentos Públicos de ${ciudad}.

TERCERO: En virtud de lo prescrito por el artículo 29 de la Ley 675 de 2001 y el reglamento de propiedad horizontal del conjunto, todos los propietarios de bienes privados están por mandato legal obligados a contribuir al pago de las expensas comunes causadas para la administración, conservación, vigilancia, aseo y mantenimiento de los bienes comunes.

CUARTO: El(la) demandado(a) ha incumplido de manera contumaz y reiterada su obligación de pago, adeudando a la fecha la suma de $${saldoCapital.toLocaleString('es-CO')} COP por concepto de capital, más $${saldoIntereses.toLocaleString('es-CO')} COP por concepto de intereses moratorios causados, para un saldo consolidado de $${cuantiaTotal.toLocaleString('es-CO')} COP (${mesesAdeudados}).

QUINTO: TÍTULO EJECUTIVO PLENO: De conformidad con el artículo 48 de la Ley 675 de 2001, la certificación del estado de cuenta de la deuda expedida por el Administrador de la copropiedad, con la constancia de la mora, PRESTA PLENO MÉRITO EJECUTIVO, constituyendo una obligación clara, expresa y actualmente exigible en los precisos términos del artículo 422 del Código General del Proceso, sin necesidad de protesto, autenticación ni reconocimiento judicial previo.

SEXTO: Mi poderdante requirió formalmente al demandado por vía prejurídica conforme a la Ley 2300 de 2023, sin que a la fecha se haya verificado el pago de la obligación ni suscrito acuerdo viable alguno.

III. FUNDAMENTOS DE DERECHO

Invoco como fundamentos jurídicos los artículos 29, 30, 48, 50 y 51 de la Ley 675 de 2001; los artículos 82, 84, 88, 422, 424, 430, 431 y concordantes del Código General del Proceso (Ley 1564 de 2012); el artículo 2469 y ss. del Código Civil; y la Ley 2213 de 2022 en materia de justicia digital.

IV. COMPETENCIA Y CUANTÍA

Por la naturaleza del asunto, el lugar de ubicación del inmueble (${ciudad}) y la cuantía de la obligación ($${cuantiaTotal.toLocaleString('es-CO')} COP), es Usted Señor(a) Juez el funcionario competente para conocer y fallar en derecho el presente litigio.

V. NOTIFICACIONES

- Demandante y Apoderado: ${abogado.direccionDespacho}. Correo electrónico judicial (Ley 2213 de 2022): ${abogado.correoJudicial}.
- Demandado(a): En el inmueble objeto de cobro: ${direccionInmueble}, ${debtor.unidad_privada}, ${ciudad}. Correo electrónico: ${debtor.email || 'No suministrado'}.

Del Señor Juez,

_________________________________________________
${abogado.nombre.toUpperCase()}
C.C. No. ${abogado.cedula}
T.P. No. ${abogado.tarjetaProfesional}`;
  }, [
    ciudad, tipoCuantia, copropiedadNombre, copropiedadNit, debtor,
    matriculaInmobiliaria, abogado, administradorNombre, saldoCapital,
    saldoIntereses, cuantiaTotal, mesesAdeudados, direccionInmueble
  ]);

  // Redacción del Cuaderno de Medidas Cautelares
  const textoMedidasCautelares = useMemo(() => {
    return `SEÑOR(A)
JUEZ CIVIL MUNICIPAL DE ${ciudad.toUpperCase()} (REPARTO)
E. S. D.

REF: SOLICITUD DE MEDIDAS CAUTELARES PREVIAS (CUADERNO SEPARADO)
PROCESO: EJECUTIVO SINGULAR DE ${tipoCuantia.toUpperCase()} CUANTÍA
DEMANDANTE: ${copropiedadNombre.toUpperCase()} (NIT: ${copropiedadNit})
DEMANDADO(A): ${debtor.propietario_nombre.toUpperCase()}

${abogado.nombre.toUpperCase()}, de las calidades conocidas en el proceso de la referencia, con fundamento en lo preceptuado en los artículos 599 y concordantes del Código General del Proceso (Ley 1564 de 2012), muy respetuosamente solicito a su Despacho que en auto separado y de manera previa o simultánea con el libramiento del Mandamiento de Pago, se decreten y practiquen las siguientes MEDIDAS CAUTELARES:

PRIMERA: EMBARGO Y SECUESTRO DEL BIEN INMUEBLE DEMANDADO:
Que se decrete el EMBARGO Y POSTERIOR SECUESTRO del bien inmueble de propiedad de la parte demandada ${debtor.propietario_nombre}, ubicado en la ${direccionInmueble}, ${debtor.unidad_privada} de la ciudad de ${ciudad}, al cual le corresponde el Folio de Matrícula Inmobiliaria No. ${matriculaInmobiliaria} de la Oficina de Registro de Instrumentos Públicos de ${ciudad} y Cédula Catastral No. ${cedulaCatastral}.
Para la efectividad de la medida, solicito se libre oficio a la Oficina de Registro de Instrumentos Públicos correspondiente, indicando los datos registrales para su debida anotación.

SEGUNDA: EMBARGO Y RETENCIÓN DE CUENTAS BANCARIAS:
Que se decrete el EMBARGO Y RETENCIÓN de las sumas de dinero que el demandado(a) ${debtor.propietario_nombre}, identificado(a) con C.C./NIT No. ${debtor.propietario_id || '[CÉDULA]'}, posea o llegue a poseer en cuentas corrientes, de ahorros, certificados de depósito a término (CDTs) o fiducias mercantiles en las siguientes entidades financieras:
${medidas.entidadesBancarias.map(b => `- ${b}`).join('\n')}
Medida que se solicita limitándola hasta por la suma de $${medidas.limiteEmbargoCuentas.toLocaleString('es-CO')} COP, suma que incluye capital reclamado, intereses futuros de mora causados durante el proceso y costas judiciales.

TERCERA: OFICIOS A LA SUPERINTENDENCIA FINANCIERA:
Solicito a su Despacho librar oficio a la Superintendencia Financiera de Colombia para que comunique a los establecimientos bancarios vigilados la orden de embargo precautelativo aquí decretada.

Atentamente,

_________________________________________________
${abogado.nombre.toUpperCase()}
C.C. No. ${abogado.cedula}
T.P. No. ${abogado.tarjetaProfesional}`;
  }, [ciudad, tipoCuantia, copropiedadNombre, copropiedadNit, debtor, abogado, direccionInmueble, matriculaInmobiliaria, cedulaCatastral, medidas]);

  // Redacción del Poder Especial para Abogado
  const textoPoderEspecial = useMemo(() => {
    return `PODER ESPECIAL, AMPLIO Y SUFICIENTE
RÉGIMEN DE PROPIEDAD HORIZONTAL (LEY 675 DE 2001) Y ART. 74 Y SS. DEL C.G.P.

En la ciudad de ${ciudad}, a los ${new Date().toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' })}, yo, ${administradorNombre.toUpperCase()}, mayor de edad y domiciliado en esta ciudad, identificado con la C.C. No. ${administradorCedula}, obrando en mi calidad de Administrador(a) y Representante Legal de la persona jurídica ${copropiedadNombre.toUpperCase()}, con NIT ${copropiedadNit}, personería jurídica reconocida conforme a la Ley 675 de 2001, confiero por medio del presente documento PODER ESPECIAL, AMPLIO Y SUFICIENTE al(a la) doctor(a) ${abogado.nombre.toUpperCase()}, mayor de edad, domiciliado(a) en ${ciudad}, identificado(a) con la Cédula de Ciudadanía No. ${abogado.cedula} y portador(a) de la Tarjeta Profesional No. ${abogado.tarjetaProfesional} del Consejo Superior de la Judicatura, para que en nombre y representación de la mencionada copropiedad, inicie, tramite y lleve hasta su total terminación PROCESO EJECUTIVO SINGULAR DE ${tipoCuantia.toUpperCase()} CUANTÍA en contra de ${debtor.propietario_nombre.toUpperCase()}, para obtener el pago forzoso de las expensas comunes ordinarias y extraordinarias, recargos e intereses de mora correspondientes a la unidad ${debtor.unidad_privada}.

Mi apoderado(a) queda expresamente facultado(a) para solicitar medidas cautelares previas de embargo y secuestro sobre bienes y cuentas bancarias, recibir, transigir, conciliar, desistir, reasumir, sustituir, interponer recursos ordinarios y extraordinarios, liquidar crédito y costas, e incoar todas las actuaciones procesales conducentes a la defensa y efectividad del crédito cobrado, en los términos del artículo 77 del Código General del Proceso.

Sírvase Señor Juez reconocer personería jurídica a mi apoderado en los términos y para los efectos del presente mandato.

PODERDANTE:                                       APODERADO:

________________________________________          ________________________________________
${administradorNombre.toUpperCase()}              ${abogado.nombre.toUpperCase()}
C.C. No. ${administradorCedula}                   C.C. No. ${abogado.cedula}
Representante Legal - ${copropiedadNombre}        T.P. No. ${abogado.tarjetaProfesional}`;
  }, [ciudad, administradorNombre, administradorCedula, copropiedadNombre, copropiedadNit, abogado, tipoCuantia, debtor]);

  // Manejar Guardar y Crear Demanda
  const handleCreateLawsuit = () => {
    const id = `DEM-${Date.now().toString().slice(-6)}`;
    const newLawsuit: ExecutiveLawsuit = {
      id,
      copropiedad_id: 'default',
      copropiedad_nombre: copropiedadNombre,
      copropiedad_nit: copropiedadNit,
      ciudad,
      departamento,
      juzgado_reparto: `Juez Civil Municipal de ${ciudad} (Reparto)`,
      administrador_nombre: administradorNombre,
      administrador_cedula: administradorCedula,
      abogado,
      unidad_privada: debtor.unidad_privada,
      propietario_nombre: debtor.propietario_nombre,
      propietario_id: debtor.propietario_id || '',
      propietario_correo: debtor.email || '',
      propietario_telefono: debtor.telefono || '',
      direccion_notificacion: `${direccionInmueble}, ${debtor.unidad_privada}`,
      capital_adeudado: saldoCapital,
      intereses_moratorios_a_la_fecha: saldoIntereses,
      cuantia_total: cuantiaTotal,
      tipo_cuantia: tipoCuantia,
      meses_adeudados_descripcion: mesesAdeudados,
      medidas_cautelares: medidas,
      anexos_verificados: anexos,
      libelo_demanda: textoDemanda,
      solicitud_medidas_cautelares: textoMedidasCautelares,
      poder_especial: textoPoderEspecial,
      estado: 'listo_para_radicar',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    onSaveLawsuit(newLawsuit);
    toast.success(`Expediente de Demanda Ejecutiva para ${debtor.unidad_privada} generado con éxito.`);
  };

  const copyCurrentDoc = () => {
    let t = textoDemanda;
    if (docTab === 'medidas') t = textoMedidasCautelares;
    if (docTab === 'poder') t = textoPoderEspecial;
    navigator.clipboard.writeText(t);
    toast.success('Documento copiado al portapapeles.');
  };

  const downloadDocTxt = () => {
    let t = textoDemanda;
    let filename = `Demanda_Ejecutiva_${debtor.unidad_privada.replace(/\s+/g, '_')}.txt`;
    if (docTab === 'medidas') {
      t = textoMedidasCautelares;
      filename = `Medidas_Cautelares_${debtor.unidad_privada.replace(/\s+/g, '_')}.txt`;
    } else if (docTab === 'poder') {
      t = textoPoderEspecial;
      filename = `Poder_Especial_${debtor.unidad_privada.replace(/\s+/g, '_')}.txt`;
    }

    const blob = new Blob([t], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
    toast.success(`Descargando ${filename}...`);
  };

  return (
    <div className="space-y-6">
      {/* Barra de Título */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-xl bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white shadow-md">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-indigo-600 text-white shadow">
            <Gavel className="h-6 w-6" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-base sm:text-lg font-bold">
                Generador de Demanda Ejecutiva Singular &amp; Medidas Cautelares
              </h2>
              <Badge className="bg-amber-500/20 text-amber-300 border-amber-400/30 text-[10px]">
                Art. 422 CGP + Art. 48 Ley 675
              </Badge>
            </div>
            <p className="text-xs text-slate-300 mt-0.5">
              Expediente judicial digital con embargo de inmueble y cuentas para {debtor.unidad_privada} ({debtor.propietario_nombre})
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onCancel}
            className="text-xs h-8 text-slate-200 border-slate-700 hover:bg-slate-800"
          >
            Cancelar
          </Button>
          <Button
            size="sm"
            onClick={handleCreateLawsuit}
            className="bg-indigo-600 hover:bg-indigo-500 text-white text-xs h-8 font-semibold gap-1.5 shadow"
          >
            <FileCheck2 className="h-3.5 w-3.5" />
            Formalizar Demanda en Bitácora
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* ── COLUMNA IZQUIERDA: CONFIGURACIÓN PROCESAL Y MEDIDAS CAUTELARES ── */}
        <div className="lg:col-span-5 space-y-4 text-xs">
          {/* Tarjeta de Resumen del Crédito */}
          <Card className="border shadow-sm bg-gradient-to-br from-indigo-50/60 to-slate-50 dark:from-indigo-950/20 dark:to-slate-900">
            <CardHeader className="pb-2.5">
              <CardTitle className="text-xs font-bold uppercase tracking-wider text-indigo-950 dark:text-indigo-200 flex items-center justify-between">
                <span className="flex items-center gap-1.5">
                  <Scale className="h-3.5 w-3.5 text-indigo-600" />
                  Liquidación de la Cuantía Judicial
                </span>
                <Badge variant="outline" className="border-indigo-300 text-indigo-700 bg-indigo-50 text-[10px]">
                  {tipoCuantia === 'minima' ? 'Mínima Cuantía (< 40 SMLMV)' : 'Menor Cuantía'}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-3 gap-2 pt-1 text-center">
                <div className="p-2 rounded-lg bg-white dark:bg-slate-900 border">
                  <span className="text-[10px] text-muted-foreground block">Capital</span>
                  <span className="font-bold text-slate-800 dark:text-slate-200">${saldoCapital.toLocaleString('es-CO')}</span>
                </div>
                <div className="p-2 rounded-lg bg-white dark:bg-slate-900 border">
                  <span className="text-[10px] text-rose-600 block">Intereses</span>
                  <span className="font-bold text-rose-600">${saldoIntereses.toLocaleString('es-CO')}</span>
                </div>
                <div className="p-2 rounded-lg bg-indigo-600 text-white shadow-xs">
                  <span className="text-[10px] text-indigo-200 block">Cuantía Total</span>
                  <span className="font-bold text-xs">${cuantiaTotal.toLocaleString('es-CO')}</span>
                </div>
              </div>

              <div className="space-y-1">
                <Label className="text-[11px] font-medium">Concepto / Periodos en Mora</Label>
                <Input
                  value={mesesAdeudados}
                  onChange={(e) => setMesesAdeudados(e.target.value)}
                  className="text-xs h-8"
                />
              </div>
            </CardContent>
          </Card>

          {/* Tarjeta de Medidas Cautelares */}
          <Card className="border shadow-sm border-rose-200 dark:border-rose-900/40">
            <CardHeader className="pb-2.5 bg-rose-50/50 dark:bg-rose-950/20 border-b border-rose-100 dark:border-rose-900/30">
              <CardTitle className="text-xs font-bold uppercase tracking-wider text-rose-900 dark:text-rose-200 flex items-center justify-between">
                <span className="flex items-center gap-1.5">
                  <ShieldAlert className="h-3.5 w-3.5 text-rose-600" />
                  Medidas Cautelares Previas (Art. 599 CGP)
                </span>
                <Badge className="bg-rose-600 text-white text-[9px]">Prioritario</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-3">
              {/* Embargo del Inmueble */}
              <div className="flex items-start justify-between p-2.5 rounded-lg border bg-muted/20">
                <div className="space-y-0.5 pr-2">
                  <Label className="text-xs font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-1.5">
                    <Building2 className="h-3.5 w-3.5 text-indigo-600" />
                    Embargo y Secuestro del Inmueble
                  </Label>
                  <p className="text-[10px] text-muted-foreground">
                    Bloquea la venta y garantiza el crédito directamente con la propiedad privada morosa.
                  </p>
                </div>
                <Switch
                  checked={medidas.embargoInmueble}
                  onCheckedChange={(checked) => setMedidas({ ...medidas, embargoInmueble: checked })}
                />
              </div>

              {medidas.embargoInmueble && (
                <div className="grid grid-cols-2 gap-2 pl-2 border-l-2 border-indigo-400">
                  <div className="space-y-1">
                    <Label className="text-[10px] font-medium">Matrícula Inmobiliaria</Label>
                    <Input
                      placeholder="50C-XXXXXXX"
                      value={matriculaInmobiliaria}
                      onChange={(e) => {
                        setMatriculaInmobiliaria(e.target.value);
                        setMedidas({ ...medidas, matriculaInmobiliaria: e.target.value });
                      }}
                      className="text-xs h-8 font-mono"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-medium">Cédula Catastral</Label>
                    <Input
                      placeholder="00-01-XXXX-XXXX"
                      value={cedulaCatastral}
                      onChange={(e) => {
                        setCedulaCatastral(e.target.value);
                        setMedidas({ ...medidas, cedulaCatastral: e.target.value });
                      }}
                      className="text-xs h-8 font-mono"
                    />
                  </div>
                </div>
              )}

              {/* Embargo de Cuentas Bancarias */}
              <div className="flex items-start justify-between p-2.5 rounded-lg border bg-muted/20">
                <div className="space-y-0.5 pr-2">
                  <Label className="text-xs font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-1.5">
                    <Landmark className="h-3.5 w-3.5 text-emerald-600" />
                    Embargo Preventivo de Cuentas Bancarias
                  </Label>
                  <p className="text-[10px] text-muted-foreground">
                    Oficios a Bancolombia, Davivienda, Bogotá, BBVA y Superfinanciera hasta el 200% de la deuda.
                  </p>
                </div>
                <Switch
                  checked={medidas.embargoCuentasBancarias}
                  onCheckedChange={(checked) => setMedidas({ ...medidas, embargoCuentasBancarias: checked })}
                />
              </div>
            </CardContent>
          </Card>

          {/* Abogado y Representación */}
          <Card className="border shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-bold uppercase tracking-wider flex items-center gap-1.5">
                <Briefcase className="h-3.5 w-3.5 text-slate-600" />
                Abogado Apoderado Judicial
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2.5 pt-1">
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-[10px]">Nombre del Abogado</Label>
                  <Input
                    value={abogado.nombre}
                    onChange={(e) => setAbogado({ ...abogado, nombre: e.target.value })}
                    className="text-xs h-8"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px]">Tarjeta Profesional No.</Label>
                  <Input
                    value={abogado.tarjetaProfesional}
                    onChange={(e) => setAbogado({ ...abogado, tarjetaProfesional: e.target.value })}
                    className="text-xs h-8 font-mono"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px]">Correo Electrónico Judicial (Ley 2213/2022)</Label>
                <Input
                  value={abogado.correoJudicial}
                  onChange={(e) => setAbogado({ ...abogado, correoJudicial: e.target.value })}
                  className="text-xs h-8 font-mono"
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ── COLUMNA DERECHA: DOCUMENTOS JUDICIALES Y EXPEDIENTE DIGITAL ── */}
        <div className="lg:col-span-7 space-y-4">
          <Card className="border shadow-sm">
            <CardHeader className="pb-2 border-b bg-muted/20">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="flex items-center gap-1.5">
                  <Button
                    variant={docTab === 'demanda' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setDocTab('demanda')}
                    className={`text-xs h-7 gap-1 ${docTab === 'demanda' ? 'bg-indigo-600 hover:bg-indigo-700' : ''}`}
                  >
                    <FileText className="h-3 w-3" /> Demanda Principal
                  </Button>
                  <Button
                    variant={docTab === 'medidas' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setDocTab('medidas')}
                    className={`text-xs h-7 gap-1 ${docTab === 'medidas' ? 'bg-indigo-600 hover:bg-indigo-700' : ''}`}
                  >
                    <ShieldAlert className="h-3 w-3" /> Medidas Cautelares
                  </Button>
                  <Button
                    variant={docTab === 'poder' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setDocTab('poder')}
                    className={`text-xs h-7 gap-1 ${docTab === 'poder' ? 'bg-indigo-600 hover:bg-indigo-700' : ''}`}
                  >
                    <UserCheck className="h-3 w-3" /> Poder Especial
                  </Button>
                  <Button
                    variant={docTab === 'expediente' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setDocTab('expediente')}
                    className={`text-xs h-7 gap-1 ${docTab === 'expediente' ? 'bg-indigo-600 hover:bg-indigo-700' : ''}`}
                  >
                    <FileCheck2 className="h-3 w-3" /> Anexos CGP
                  </Button>
                </div>

                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={copyCurrentDoc}
                    className="h-7 text-xs gap-1 border-slate-300"
                  >
                    <Copy className="h-3 w-3" /> Copiar
                  </Button>
                  <Button
                    size="sm"
                    onClick={downloadDocTxt}
                    className="h-7 text-xs gap-1 bg-slate-900 hover:bg-slate-800 text-white dark:bg-slate-100 dark:text-slate-900 font-semibold"
                  >
                    <Download className="h-3 w-3" /> Descargar TXT
                  </Button>
                </div>
              </div>
            </CardHeader>

            <CardContent className="p-0">
              {docTab === 'expediente' ? (
                /* Checklist del Expediente Digital */
                <div className="p-5 space-y-4 text-xs">
                  <div>
                    <h3 className="font-bold text-sm text-slate-900 dark:text-white flex items-center gap-2">
                      <FileCheck2 className="h-4 w-4 text-indigo-600" />
                      Checklist del Expediente Digital (Art. 84 CGP)
                    </h3>
                    <p className="text-muted-foreground text-[11px] mt-0.5">
                      Paquete probatorio completo para radicación inmediata en la Rama Judicial (correo o portal SIUGJ).
                    </p>
                  </div>

                  <div className="space-y-2.5">
                    {[
                      { key: 'poderEspecial', label: '1. Poder especial con facultades expresas de transigir y medidas cautelares (Art. 74 CGP)' },
                      { key: 'certificadoExistenciaAlcaldia', label: '2. Certificado de existencia y representación legal de la copropiedad (Alcaldía Local)' },
                      { key: 'tituloEjecutivoCertificacion', label: '3. Certificación de deuda expedida por la Administración (Título Ejecutivo - Art. 48 Ley 675)' },
                      { key: 'certificadoTradicionLibertad', label: '4. Certificado de Tradición y Libertad del folio de matrícula inmobiliaria' },
                      { key: 'liquidacioCreditoDetallada', label: '5. Liquidación detallada del crédito con intereses moratorios mes a mes' },
                      { key: 'pruebaEnvioNotificacionLey2213', label: '6. Constancia de envío simultáneo por correo electrónico al demandado (Ley 2213 de 2022)' },
                    ].map((item) => (
                      <div key={item.key} className="flex items-center justify-between p-2.5 rounded-lg border bg-slate-50 dark:bg-slate-900/50">
                        <span className="font-medium text-slate-800 dark:text-slate-200">{item.label}</span>
                        <Badge className="bg-emerald-600 text-white text-[10px]">VERIFICADO</Badge>
                      </div>
                    ))}
                  </div>

                  <div className="p-3 rounded-lg border bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-900 text-[11px] text-blue-900 dark:text-blue-200 flex items-center justify-between">
                    <span>Estado del expediente para radicación:</span>
                    <strong className="font-bold text-emerald-600">100% Completo y Acreditado</strong>
                  </div>
                </div>
              ) : (
                /* Visor del Documento Jurídico */
                <div className="p-5 font-mono text-xs text-slate-800 dark:text-slate-200 whitespace-pre-wrap leading-relaxed max-h-[580px] overflow-y-auto bg-slate-50 dark:bg-slate-900/60 rounded-b-lg">
                  {docTab === 'demanda' && textoDemanda}
                  {docTab === 'medidas' && textoMedidasCautelares}
                  {docTab === 'poder' && textoPoderEspecial}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
export default ExecutiveLawsuitGenerator;
